import React, { useMemo } from 'react';
import { Card, Divider, SimpleGrid, Stack, Text } from '@mantine/core';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiPage, {
  ChartCard,
  ChartTooltip,
  QueryState,
  StatTile,
  fmtInt,
  fmtMoney,
  fmtPct,
  useChartColors,
} from '../KpiPage';
import { kpiBySlug } from '../kpiCatalog';
import { useWindowParams } from '../DateWindowSelector';
import useNexusQuery, { field, num } from '../../../hooks/useNexusQuery';

const kpi = kpiBySlug('ssp-deviation');

const BAND_TONE = { 1: 'bad', 2: 'bad', 3: 'warn', 4: 'good', 5: 'good' };
const SSP_STATUS_ORDER = ['SSP current', 'SSP expired', 'SSP, no priced date', 'No SSP'];

function SectionHeading({ title, subtitle }) {
  return (
    <Stack gap={4} mt="lg">
      <Text fw={800} size="xl">
        {title}
      </Text>
      {subtitle && (
        <Text size="sm" c="dimmed">
          {subtitle}
        </Text>
      )}
      <Divider mt={2} />
    </Stack>
  );
}

export default function SspDeviation() {
  const c = useChartColors();
  const [window, setWindow] = useWindowParams();

  const salesCoverage = useNexusQuery('initiative-managed-sales-coverage', window);
  const sspStatusQ = useNexusQuery('initiative-managed-sales-ssp-status', window);
  const summary = useNexusQuery('initiative-ssp-deviation-summary', window);
  const bands = useNexusQuery('initiative-ssp-deviation-bands', window);
  const byCondition = useNexusQuery('initiative-ssp-deviation-by-condition', window);
  const trend = useNexusQuery('initiative-ssp-deviation-trend', window);
  const unscored = useNexusQuery('initiative-ssp-unscored-lines', window);

  // --- Section A: what we sold, and what's price-checkable ---
  const salesCov = useMemo(() => {
    const r = salesCoverage.rows[0];
    const g = (k) => (r ? num(r, k) : 0);
    return {
      total: g('TotalManagedParts'),
      sold: g('DistinctPartsSold'),
      soldPct: g('TotalManagedParts') ? (100 * g('DistinctPartsSold')) / g('TotalManagedParts') : 0,
      lines: g('Lines'),
      allLines: g('AllLines'),
      linePct: g('AllLines') ? (100 * g('Lines')) / g('AllLines') : 0,
      qty: g('Qty'),
      allQty: g('AllQty'),
      qtyPct: g('AllQty') ? (100 * g('Qty')) / g('AllQty') : 0,
    };
  }, [salesCoverage.rows]);

  const sspStatus = useMemo(() => {
    const qtyMap = {};
    const revMap = {};
    sspStatusQ.rows.forEach((r) => {
      const s = field(r, 'SspStatus');
      qtyMap[s] = num(r, 'Qty');
      revMap[s] = num(r, 'Revenue');
    });
    const data = SSP_STATUS_ORDER.filter((s) => qtyMap[s] != null).map((s) => ({
      status: s,
      qty: qtyMap[s],
      rev: revMap[s] ?? 0,
    }));
    const totalQty = data.reduce((s, d) => s + d.qty, 0);
    const priced = data.filter((d) => d.status !== 'No SSP').reduce((s, d) => s + d.qty, 0);
    return { data, pricedPct: totalQty ? (100 * priced) / totalQty : 0 };
  }, [sspStatusQ.rows]);

  const sspColor = (s) =>
    ({
      'SSP current': c.positive,
      'SSP expired': c.negative,
      'SSP, no priced date': c.series[2],
      'No SSP': c.neutral,
    }[s] ?? c.neutral);

  // --- Section B: deviation ---
  const condData = useMemo(
    () =>
      byCondition.rows
        .map((r) => ({
          condition: field(r, 'Condition'),
          lines: num(r, 'Lines'),
          avgDev: num(r, 'AvgDeviationPct'),
        }))
        .sort((a, b) => b.lines - a.lines),
    [byCondition.rows]
  );

  const bandData = useMemo(
    () =>
      bands.rows.map((r) => ({
        band: field(r, 'Band'),
        lines: num(r, 'Lines'),
        tone: BAND_TONE[num(r, 'SortOrder')] ?? 'good',
      })),
    [bands.rows]
  );

  const trendData = useMemo(
    () =>
      trend.rows.map((r) => ({
        month: field(r, 'MonthKey'),
        leakagePolicy: num(r, 'FloorLeakageDollars'),
        // the amount SSP-leakage exceeds policy-leakage = discount policy already allows for aged stock.
        leakageAllowed: Math.max(0, num(r, 'LeakageDollars') - num(r, 'FloorLeakageDollars')),
        rate: num(r, 'ExceptionRatePct'),
        ratePolicy: num(r, 'FloorExceptionRatePct'),
      })),
    [trend.rows]
  );

  const totals = useMemo(() => {
    const s = summary.rows[0];
    const g = (k) => (s ? num(s, k) : 0);
    const unscoredLines = unscored.rows.reduce((sum, r) => sum + num(r, 'Lines'), 0);
    return {
      validLines: g('ValidLines'),
      belowLines: g('BelowSspLines'),
      leakage: g('LeakageDollars'),
      exceptionRate: g('ExceptionRatePct'),
      belowFloorLines: g('BelowFloorLines'),
      floorRate: g('FloorExceptionRatePct'),
      floorLeakage: g('FloorLeakageDollars'),
      avgDeviationPct: g('AvgDeviationPct'),
      avgDos: g('AvgDosDays'),
      unscoredLines,
    };
  }, [summary.rows, unscored.rows]);

  const toneColor = { bad: c.negative, warn: c.series[3], good: c.positive };
  const sanctioned = totals.leakage - totals.floorLeakage; // authorized DOS discount, not leakage

  return (
    <KpiPage kpi={kpi} window={window} onWindowChange={setWindow} hideMethodology>
      <Stack gap="md">
        {/* ============================ SECTION A — WHAT WE SOLD ================================= */}
        <SectionHeading
          title="What we sold, and what we can price-check"
          subtitle="Managed parts as a share of all sales over the window, and how much of it had a sell price (at the time of sale) to measure against."
        />

        <QueryState isLoading={salesCoverage.isLoading} error={salesCoverage.error} isEmpty={!salesCov.total}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="Managed PNs sold"
              value={fmtPct(salesCov.soldPct)}
              sub={`${fmtInt(salesCov.sold)} of ${fmtInt(salesCov.total)} part numbers`}
              color={c.series[0]}
            />
            <StatTile
              label="Share of sale lines"
              value={fmtPct(salesCov.linePct)}
              sub={`${fmtInt(salesCov.lines)} of ${fmtInt(salesCov.allLines)} lines are managed`}
              color={c.series[0]}
            />
            <StatTile
              label="Share of quantity"
              value={fmtPct(salesCov.qtyPct)}
              sub={`${fmtInt(salesCov.qty)} of ${fmtInt(salesCov.allQty)} units`}
              color={c.series[0]}
            />
            <StatTile
              label="Had an SSP at sale"
              value={fmtPct(sspStatus.pricedPct)}
              sub="Price-checkable share, by quantity"
              color={c.positive}
            />
          </SimpleGrid>
        </QueryState>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="Managed sales by SSP status at sale — by quantity"
            caption="Point-in-time: did an SSP exist for the condition on the sale date, and was it current? By units, pricing coverage looks healthy — 'No SSP' is a sliver."
            height={260}
          >
            <QueryState isLoading={sspStatusQ.isLoading} error={sspStatusQ.error} isEmpty={!sspStatus.data.length}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sspStatus.data}
                    dataKey="qty"
                    nameKey="status"
                    innerRadius="50%"
                    outerRadius="78%"
                    paddingAngle={2}
                  >
                    {sspStatus.data.map((d) => (
                      <Cell key={d.status} fill={sspColor(d.status)} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => `${fmtInt(v)} units`} />}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="Managed sales by SSP status at sale — by revenue"
            caption="The same split by dollars — and it shifts: expired and 'No SSP' take a bigger share of revenue than of units. A few big-ticket parts sold with no current SSP carry real money."
            height={260}
          >
            <QueryState isLoading={sspStatusQ.isLoading} error={sspStatusQ.error} isEmpty={!sspStatus.data.length}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={sspStatus.data}
                    dataKey="rev"
                    nameKey="status"
                    innerRadius="50%"
                    outerRadius="78%"
                    paddingAngle={2}
                  >
                    {sspStatus.data.map((d) => (
                      <Cell key={d.status} fill={sspColor(d.status)} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={<ChartTooltip formatter={fmtMoney} />}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Legend verticalAlign="bottom" iconType="circle" />
                </PieChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>

        {/* ============================ SECTION B — DEVIATION =================================== */}
        <SectionHeading
          title="Deviation on the priced lines"
          subtitle="Of the managed lines that carry a sell price, how often and how far we sold below the SSP in effect at the time of sale — shown against the list SSP and against the pricing policy."
        />

        <Card withBorder radius="md" p="md">
          <Stack gap="sm">
            <Text fw={600}>SSP vs pricing policy, plainly</Text>
            <Text size="xs" c="dimmed">
              Point-in-time, managed parts only. <b>Below SSP</b> = we sold under the list price.
              <b> Below pricing policy</b> = we sold under even the approved days-on-shelf (DOS)
              discount for aged stock — that&apos;s true leakage. The gap between them is discounting
              that policy already allows.
            </Text>
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <Stack gap={4}>
                <Text size="sm" fw={600} c={c.series[3]}>
                  Below SSP — off the list price
                </Text>
                <Text size="sm">
                  Any line sold under the at-sale SSP. Counts policy-allowed aging discounts too, so it
                  overstates the discipline problem on its own.
                </Text>
              </Stack>
              <Stack gap={4}>
                <Text size="sm" fw={600} c={c.negative}>
                  Below pricing policy — true leakage
                </Text>
                <Text size="sm">
                  Under <Text span ff="monospace">SSP × the DOS discount</Text> (0–60d 0% · 61–120d 5% ·
                  121–180d 10% · 181–270d 12% · 271+d 15%). Life-limited/min-price units get no DOS
                  discount and are an approximation here.
                </Text>
              </Stack>
            </SimpleGrid>
          </Stack>
        </Card>

        <QueryState isLoading={summary.isLoading} error={summary.error} isEmpty={!summary.rows.length}>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
            <StatTile
              label="Below SSP"
              value={fmtPct(totals.exceptionRate)}
              sub={`${fmtInt(totals.belowLines)} of ${fmtInt(totals.validLines)} lines — off list price`}
              color={c.series[3]}
            />
            <StatTile
              label="Below pricing policy"
              value={fmtPct(totals.floorRate)}
              sub={`${fmtInt(totals.belowFloorLines)} lines — true leakage after DOS discount`}
              color={c.negative}
            />
            <StatTile label="Leakage vs SSP" value={fmtMoney(totals.leakage)} sub="Below the list price" color={c.series[3]} />
            <StatTile
              label="True leakage (vs policy)"
              value={fmtMoney(totals.floorLeakage)}
              sub="Below the pricing policy"
              color={c.negative}
            />
          </SimpleGrid>
        </QueryState>

        <Text size="xs" c="dimmed">
          Avg deviation {fmtPct(totals.avgDeviationPct)} · {fmtInt(totals.unscoredLines)} unscored lines
          (no SSP at the sale date) · {fmtMoney(sanctioned)} of the SSP gap is authorized aging discount,
          not leakage. Average days-on-shelf at sale: {fmtInt(totals.avgDos)} — most managed stock sells
          old enough to reach the deepest policy tier.
        </Text>

        <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
          <ChartCard
            title="Deviation depth distribution (line level)"
            caption="How often, and how far below the at-sale SSP. 'Within 2% of SSP' is a near-SSP depth bucket, not the exception line."
          >
            <QueryState isLoading={bands.isLoading} error={bands.error} isEmpty={!bandData.length}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bandData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="band" stroke={c.axis} fontSize={10} interval={0} />
                  <YAxis stroke={c.axis} fontSize={12} width={48} />
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => `${fmtInt(v)} lines`} />}
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Bar dataKey="lines" name="Sold lines" radius={[4, 4, 0, 0]}>
                    {bandData.map((d) => (
                      <Cell key={d.band} fill={toneColor[d.tone]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>

          <ChartCard
            title="Deviation by condition"
            caption="Average deviation from the at-sale SSP by condition. As-removed (AR) is where discipline breaks down — repaired SV/OH sell at or above SSP on average, AR sells well below (the take-home-value problem)."
          >
            <QueryState isLoading={byCondition.isLoading} error={byCondition.error} isEmpty={!condData.length}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={condData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                  <XAxis dataKey="condition" stroke={c.axis} fontSize={12} />
                  <YAxis stroke={c.axis} fontSize={12} width={48} tickFormatter={(v) => `${Math.round(v)}%`} />
                  <ReferenceLine y={0} stroke={c.axis} />
                  <Tooltip
                    content={<ChartTooltip formatter={(v) => fmtPct(v)} />}
                    cursor={{ fill: c.grid, opacity: 0.3 }}
                    wrapperStyle={{ outline: 'none' }}
                  />
                  <Bar dataKey="avgDev" name="Avg deviation %" radius={[4, 4, 0, 0]}>
                    {condData.map((d) => (
                      <Cell key={d.condition} fill={d.avgDev < 0 ? c.negative : c.positive} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </QueryState>
          </ChartCard>
        </SimpleGrid>

        <ChartCard
          title="Leakage dollars and exception rate over time"
          caption="Leakage stacked: true leakage (below policy, red) plus the aging discount policy allows (amber) — together they are the total below-SSP leakage. Exception rate vs SSP (solid) and vs policy (dashed) as lines."
          height={260}
        >
          <QueryState isLoading={trend.isLoading} error={trend.error} isEmpty={!trendData.length}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={trendData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                <XAxis dataKey="month" stroke={c.axis} fontSize={10} />
                <YAxis yAxisId="left" stroke={c.axis} fontSize={12} tickFormatter={(v) => fmtMoney(v)} width={56} />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke={c.axis}
                  fontSize={12}
                  tickFormatter={(v) => `${Math.round(v)}%`}
                  width={44}
                />
                <Tooltip
                  content={
                    <ChartTooltip
                      formatter={(v, e) =>
                        String(e.dataKey).toLowerCase().includes('leakage') ? fmtMoney(v) : fmtPct(v)
                      }
                    />
                  }
                  cursor={{ fill: c.grid, opacity: 0.3 }}
                  wrapperStyle={{ outline: 'none' }}
                />
                <Legend iconType="circle" />
                <Bar yAxisId="left" dataKey="leakagePolicy" name="True leakage (below policy)" stackId="leak" fill={c.negative} />
                <Bar yAxisId="left" dataKey="leakageAllowed" name="Allowed aging discount" stackId="leak" fill={c.series[3]} radius={[4, 4, 0, 0]} />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="rate"
                  name="Rate vs SSP"
                  stroke={c.series[0]}
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="ratePolicy"
                  name="Rate vs policy"
                  stroke={c.series[2]}
                  strokeWidth={2}
                  strokeDasharray="4 2"
                  dot={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </QueryState>
        </ChartCard>
      </Stack>
    </KpiPage>
  );
}
